import { DisclaimerAssumptionsButton } from "@components/buttons/disclaimer-assumptions";
import { SHContainer, SHStack, SHTypography } from "@components/design-systems";
import { FeeCalculatorDisclaimerDialog } from "@components/dialogs/fee-calculator";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { useUserPermissions } from "@hooks/userUserPermission";
import { StepCompProps, StepCompRef } from "@layouts/stepper";
import { StepName } from "@layouts/stepper/step-name";
import { AnalysisUserType } from "@models/platform-analysis/entities/analysis";
import { DisclaimerDTO } from "@models/platform-analysis/entities/disclaimer";
import { AnalysisFeeCalculatorDTO } from "@models/platform-analysis/entities/steps/fee";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { AnalysisSubStep } from "@models/platform-analysis/enums/subStep";
import { ConfirmDiscardEmptyBalancesDialog } from "@pages/platform-analysis-v2/_id/components/dialogs/confirm-discard-empty-balances";
import InvalidPortfolioWarningDialog from "@pages/platform-analysis-v2/_id/components/dialogs/invalid-portfolio-warning-dialog";
import { AnalysisFeeCalculatorSkeleton } from "@pages/platform-analysis-v2/_id/components/skeletons/fee-calculator";
import {
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router";
import { useToggle } from "react-use";
import { FeeCalculatorSection } from "./components/section";
import { FeeCalculatorFormValidStatesTracker } from "./components/trackers/form-valid-states-tracker";
import { AnalysisFeeCalculatorRenderStepSectionsConfig } from "./configs";
import { normalizeFeeCalculatorFormData } from "./utils";

export const FeeCalculatorStep: ForwardRefRenderFunction<
  StepCompRef,
  StepCompProps
> = ({ stepNumber }: StepCompProps, ref) => {
  const { analysisId } = useParams<{ analysisId: string }>();
  const { isSupplierGroup } = useUserPermissions();
  const userType: AnalysisUserType = isSupplierGroup ? "supplier" : "adviser";

  const {
    isDirty,
    isValid,
    isCompleted,
    isReadOnly,
    isValidPortfolioSetups,
    loadPlatformAnalysisDisclaimers,
    loadFeeCalculator,
    updateFeeCalculator,
    setFeeCalculator,
    setShowSaveProgressWarning,
  } = usePlatformAnalysis();

  const formMethods = useForm<AnalysisFeeCalculatorDTO>({
    mode: "onChange",
    defaultValues: new AnalysisFeeCalculatorDTO(),
  });

  useImperativeHandle(ref, () => ({
    onChangeStep: async (newStep) => {
      if (isCompleted || isReadOnly) return true;

      const stepKey = newStep?.key as AnalysisStep | AnalysisSubStep;
      const isBack = [
        AnalysisStep.BusinessMetric,
        AnalysisStep.Feature,
        AnalysisStep.Setup,
      ].includes(stepKey as AnalysisStep);
      const isNext = stepKey === AnalysisSubStep.Analysis;

      const shouldSubmit =
        (((!isNext && isValid) || isNext) && isDirty) || hasRecalculate;

      if (isBack && shouldSubmit && !isValidPortfolioSetups) {
        setShowSaveProgressWarning(true);

        return false;
      }

      if (!shouldSubmit) {
        if (!isNext) return true;
        if (isNext && isValidPortfolioSetups) return true;
      }

      return await handleOnSubmit();
    },
    onChangeOtherStep: async () => {
      if (isCompleted || isReadOnly) return true;
      const shouldSubmit = isValid && isDirty;

      if (!shouldSubmit) return true;

      if (shouldSubmit && !isValidPortfolioSetups) {
        setShowSaveProgressWarning(true);

        return false;
      }

      return await handleOnSubmit();
    },
    onSubmit: async () => {
      return await handleOnSubmit();
    },
  }));

  // #region States & Values
  const [isLoading, toggleIsLoading] = useToggle(true);
  const [isSubmitting, toggleIsSubmitting] = useToggle(false);
  const [hasRecalculate, toggleRecalculateData] = useToggle(false);
  const [showDisclaimerDialog, toggleDisclaimerDialog] = useToggle(false);
  const [disclaimerData, setDisclaimerData] = useState<DisclaimerDTO[]>();
  const [discardEmptyBalanceDialog, setDiscardEmptyBalanceDialog] = useState<
    { callback: (value: boolean | PromiseLike<boolean>) => void } | undefined
  >(undefined);
  const [
    isOpenInvalidPortfolioWarningDialog,
    toggleIsOpenInvalidPortfolioWarningDialog,
  ] = useToggle(false);
  // #endregion States & Values

  // #region Methods
  const loadFeeCalculatorData = async (id: string) => {
    const [feeCalculator, disclaimers] = await Promise.all([
      loadFeeCalculator({ platformAnalysisId: id }),
      loadPlatformAnalysisDisclaimers(id),
    ]);

    if (!feeCalculator || !disclaimers) return;

    const normalizedData = normalizeFeeCalculatorFormData({
      originalData: feeCalculator,
      removeUnassignedPortfolios: false,
    });
    formMethods.reset(normalizedData);
    toggleRecalculateData(!!normalizedData.hasRecalculate);
    setDisclaimerData(disclaimers);
    toggleIsLoading(!feeCalculator);
  };

  const getConfirmDiscardEmptyBalance = async (): Promise<boolean> => {
    return await new Promise((res) => {
      setDiscardEmptyBalanceDialog({ callback: res });
    });
  };

  const handleOnSubmit = async () => {
    toggleIsSubmitting(true);
    try {
      if (!analysisId || analysisId === "new" || !isValid) return false;
      if (!isDirty && !hasRecalculate) return true;

      const formData: AnalysisFeeCalculatorDTO = structuredClone(
        formMethods.getValues(),
      );

      const hasEmptyBalance = formData?.familyMembers?.some(
        (member) =>
          member.totalBalance <= 0 ||
          member.familyMemberAccounts?.some((account) => account.balance <= 0),
      );

      if (hasEmptyBalance) {
        const confirmDiscard = await getConfirmDiscardEmptyBalance();
        setDiscardEmptyBalanceDialog(undefined);
        if (!confirmDiscard) return false;
      }

      if (!isValidPortfolioSetups) {
        toggleIsOpenInvalidPortfolioWarningDialog();

        return false;
      }

      const isSuccess = await updateFeeCalculator({
        platformAnalysisId: analysisId,
        formData,
      });

      if (!isSuccess) return false;

      const normalizedData = normalizeFeeCalculatorFormData({
        originalData: formData,
        resetHasReCalculateData: true,
      });

      setFeeCalculator(normalizedData);
      formMethods.reset(normalizedData);

      return true;
    } finally {
      toggleIsSubmitting(false);
    }
  };
  // #endregion Methods

  // #region Effects
  useEffect(() => {
    if (!analysisId || analysisId === "new") return;
    loadFeeCalculatorData(analysisId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId]);
  // #endregion Effects

  if (isLoading) return <AnalysisFeeCalculatorSkeleton />;

  return (
    <FormProvider {...formMethods}>
      <form>
        <FeeCalculatorFormValidStatesTracker />

        <SHContainer sx={{ px: { xs: "16px", lg: 0 }, py: "25px" }}>
          <StepName stepNumber={stepNumber} name="Fee calculator" />

          {/* Welcome */}
          <SHStack spacing={"15px"} py="25px">
            <SHTypography variant="subtitle1">Welcome!</SHTypography>
            <SHTypography variant="body4">
              You can fill as much or as little of the fee calculator as you
              like. We will use the information you provide to simulate an
              estimated cost for each platform. Most fields are not compulsory,
              but the more you fill out, the more comprehensive our fee
              estimates will be.
            </SHTypography>
            <SHTypography variant="body4" fontSize={"10px"}>
              Any result provided by this calculator is illustrative only. It
              relies upon information and assumptions that are subject to
              change. If varied, this information and assumptions may change the
              result. Any result is not intended to be relied on for the purpose
              of making a decision about a particular financial product,
              platform or provider.
            </SHTypography>
            <SHTypography variant="body4" fontSize={"10px"}>
              By using this calculator, you confirm that you have read and
              accepted the {"  "}
              <DisclaimerAssumptionsButton
                onClick={() => toggleDisclaimerDialog(true)}
              />
            </SHTypography>
          </SHStack>
          {/* end Welcome */}

          {/* Fee calculator sections */}
          {AnalysisFeeCalculatorRenderStepSectionsConfig.map(
            (sectionConfig, index) => {
              return (
                <FeeCalculatorSection
                  disabled={isSubmitting || isReadOnly || isCompleted}
                  key={index}
                  sectionConfig={sectionConfig}
                  userType={userType}
                />
              );
            },
          )}
          {/* end Fee calculator sections */}
        </SHContainer>
      </form>

      <FeeCalculatorDisclaimerDialog
        open={showDisclaimerDialog}
        data={disclaimerData}
        onClose={() => toggleDisclaimerDialog(false)}
      />

      {discardEmptyBalanceDialog && (
        <ConfirmDiscardEmptyBalancesDialog
          onClose={() => discardEmptyBalanceDialog.callback(false)}
          onSubmit={() => {
            discardEmptyBalanceDialog.callback(true);
          }}
        />
      )}

      <InvalidPortfolioWarningDialog
        open={isOpenInvalidPortfolioWarningDialog}
        onReturn={toggleIsOpenInvalidPortfolioWarningDialog}
      />
    </FormProvider>
  );
};
